import 'reflect-metadata';
import './sentry';
import {
  ScrollToTop,
  SnackbarProvider,
  ThemeProvider,
} from '@pflegenavi/web-components';
import React, { type FC, type PropsWithChildren, StrictMode } from 'react';
import { BrowserRouter } from 'react-router-dom';

import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import App from './app/App';
import { initI18N, LocaleProvider } from '@pflegenavi/frontend/localization';
import {
  localeStorage,
  LocalizationProvider,
  ThemeLocalization,
} from '@pflegenavi/frontend/localization-web-app';
import { AuthenticationProvider } from '@pflegenavi/frontend/authentication';
import ErrorBoundary from './components/general/ErrorBoundry';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { keycloak, login } from './keycloak';

// this is a virtual font file from vite pluging see: https://github.com/stafyniaksacha/vite-plugin-fonts
import 'unfonts.css';
import { TenantContextProvider } from '@pflegenavi/frontend/tenant';
import {
  AnalyticsApiProvider,
  FamilyMemberApiProvider,
  ImageApiProvider,
  PaymentApiProvider,
  ProfileTypeContext,
  ResidentApiProvider,
  TransactionApiProvider,
  TransactionPhoenixApiProvider,
  UserProfileApiProvider,
  FamilyMemberPhoenixApiProvider,
  PaymentPhoenixApiProvider,
  ResidentPhoenixApiProvider,
  UserProfilePhoenixApiProvider,
  ReceiptsPhoenixApiProvider,
} from '@pflegenavi/frontend/api-nursing-home';
import { FMContextProvider } from '@pflegenavi/frontend/family-member-context';
import { createRoot } from 'react-dom/client';
import {
  getMobileOSWithSizeHeuristic,
  getTokenPayloadFromToken,
  getWebWelcomeToken,
  MobileOS,
} from '@pflegenavi/shared/utils';
import { DownloadAppFromStore } from './components/general/DownloadAppFromStore';
import { fmAppMainPages } from '@pflegenavi/frontend/routing';
import { ClientStoredResourcesProvider } from '@pflegenavi/frontend/client-stored-resources-context';
import {
  PflegenaviAuthenticationProvider,
  shouldUseNewLogin,
  useAuthentication,
} from '@pflegenavi/shared-frontend/authentication';
import { getPhoenixApiBaseUrl } from '@pflegenavi/shared-frontend/platform';
import * as Sentry from '@sentry/react';
import { setSentryAuthContext, setSentryApiUrl } from './sentry';
import { useAuthenticationInternal } from '@pflegenavi/frontend/authentication';

window.global ||= window;
// @ts-expect-error // pflegenaviApplication is untyped
window.global.pflegenaviApplication = 'family-member-web';
// @ts-expect-error // pflegenaviApplicationVersion is untyped
window.global.pflegenaviApplicationVersion =
  // @ts-expect-error // SENTRY_RELEASE is untyped
  typeof SENTRY_RELEASE !== 'undefined' ? SENTRY_RELEASE.id : 'local';

await initI18N(localeStorage);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 0, //1000 * 60 * 5, // 5 minutes
    },
    mutations: {
      onError: (error, variables, context) => {
        Sentry.captureException(error);
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      Sentry.captureException(error);
    },
  }),
});

const container = document.getElementById('root');
const root = createRoot(container!);

function redirectIfNecessary(url: string) {
  const hostname = new URL(url).hostname;

  if (hostname !== window.location.hostname) {
    window.location.replace(url);
    return 'redirected';
  }
  return undefined;
}

// Component to set global auth context for Sentry feedback
const SetSentryAuth: FC<PropsWithChildren> = ({ children }) => {
  const authContext = useAuthenticationInternal();
  React.useEffect(() => {
    setSentryAuthContext(authContext);
  }, [authContext]);
  return <>{children}</>;
};

const AuthWrapper: FC<PropsWithChildren> = ({ children }) => {
  const auth = useAuthentication(shouldUseNewLogin());

  return (
    <AuthenticationProvider
      // @ts-expect-error // keycloak type mismatch
      keycloak={auth ?? keycloak}
      login={login}
      redirectUri={`${window.location.origin}/${fmAppMainPages.DASHBOARD}`}
      isNewAuth={Boolean(auth)}
    >
      <SetSentryAuth>{children}</SetSentryAuth>
    </AuthenticationProvider>
  );
};

function renderApp() {
  const mobileOs = getMobileOSWithSizeHeuristic();
  if (mobileOs === MobileOS.Android || mobileOs === MobileOS.iOS) {
    root.render(
      <ThemeProvider>
        <LocaleProvider localeStorage={localeStorage}>
          <LocalizationProvider>
            <ThemeLocalization>
              <DownloadAppFromStore platform={mobileOs} />
            </ThemeLocalization>
          </LocalizationProvider>
        </LocaleProvider>
      </ThemeProvider>
    );
  } else {
    const apiUrl = getPhoenixApiBaseUrl(undefined);
    setSentryApiUrl(apiUrl);

    root.render(
      <ClientStoredResourcesProvider storage={localStorage}>
        <PflegenaviAuthenticationProvider
          storage={localStorage}
          getWelcomeToken={getWebWelcomeToken}
          shouldUseNewLogin={shouldUseNewLogin()}
          apiUrl={apiUrl}
          redirectIfNecessary={redirectIfNecessary}
          onLogout={() => queryClient.clear()}
        >
          <AuthWrapper>
            <StrictMode>
              <BrowserRouter>
                <SnackbarProvider>
                  <QueryClientProvider client={queryClient}>
                    <TenantContextProvider>
                      <PaymentApiProvider>
                        <PaymentPhoenixApiProvider>
                          <AnalyticsApiProvider>
                            <TransactionApiProvider>
                              <TransactionPhoenixApiProvider>
                                <ResidentApiProvider>
                                  <ResidentPhoenixApiProvider>
                                    <ReceiptsPhoenixApiProvider>
                                      <ImageApiProvider>
                                        <FamilyMemberPhoenixApiProvider>
                                          <ProfileTypeContext.Provider value="familyMember">
                                            <UserProfileApiProvider>
                                              <UserProfilePhoenixApiProvider>
                                                <FamilyMemberApiProvider>
                                                  <FMContextProvider>
                                                    <ThemeProvider>
                                                      <LocaleProvider
                                                        localeStorage={
                                                          localeStorage
                                                        }
                                                      >
                                                        <LocalizationProvider>
                                                          <ThemeLocalization>
                                                            <DndProvider
                                                              backend={
                                                                HTML5Backend
                                                              }
                                                            >
                                                              <ScrollToTop />
                                                              <ErrorBoundary>
                                                                <App />
                                                              </ErrorBoundary>
                                                            </DndProvider>
                                                          </ThemeLocalization>
                                                        </LocalizationProvider>
                                                      </LocaleProvider>
                                                    </ThemeProvider>
                                                  </FMContextProvider>
                                                </FamilyMemberApiProvider>
                                              </UserProfilePhoenixApiProvider>
                                              <ReactQueryDevtools
                                                initialIsOpen={false}
                                              />
                                            </UserProfileApiProvider>
                                          </ProfileTypeContext.Provider>
                                        </FamilyMemberPhoenixApiProvider>
                                      </ImageApiProvider>
                                    </ReceiptsPhoenixApiProvider>
                                  </ResidentPhoenixApiProvider>
                                </ResidentApiProvider>
                              </TransactionPhoenixApiProvider>
                            </TransactionApiProvider>
                          </AnalyticsApiProvider>
                        </PaymentPhoenixApiProvider>
                      </PaymentApiProvider>
                    </TenantContextProvider>
                  </QueryClientProvider>
                </SnackbarProvider>
              </BrowserRouter>
            </StrictMode>
          </AuthWrapper>
        </PflegenaviAuthenticationProvider>
      </ClientStoredResourcesProvider>
    );
  }
}

function redirectWelcome() {
  const queryParams = new URLSearchParams(window.location.search);
  const key = queryParams.get('key');
  let basePath = 'https://auth.pflegenavi.at';

  if (window.location.host === 'localhost:3001') {
    basePath = 'http://localhost:5051';
  } else if (
    window.location.hostname === 'family-member.staging.pflegenavi.at'
  ) {
    basePath = 'https://auth-staging.pflegenavi.at';
  }

  window.location.href = `${basePath}/auth/realms/family_member/login-actions/action-token?key=${key}`;
}

if (window.location.pathname !== '/welcome') {
  renderApp();
} else {
  const search = new URLSearchParams(window.location.search);
  const token = search.get('key');
  const payload = getTokenPayloadFromToken(token);
  if (token && payload?.v !== 'pn0.1') {
    redirectWelcome();
  } else {
    renderApp();
  }
}
